import { cn } from '@/lib/utils';
import { useUnitSystem } from '@/hooks/useUnitSystem';
import { useZoneSystem } from '@/hooks/useClosedLoopAnalytics';

interface PaceZonesHudProps {
  className?: string;
}

// Polarized target % by zone number (Seiler 2010): 75% easy / 7% moderate / 18% hard
const POLARIZED_TARGET_PCT: Record<number, number> = { 1: 30, 2: 45, 3: 7, 4: 12, 5: 6 };

const FALLBACK_ZONES = [
  { id: 1, label: 'Z1 · EASY', paceRange: '7:00–7:40', pct: 30, color: 'hsl(170, 80%, 45%)' },
  { id: 2, label: 'Z2 · AEROBIC', paceRange: '5:50–7:00', pct: 45, color: 'hsl(160, 70%, 50%)' },
  { id: 3, label: 'Z3 · TEMPO', paceRange: '5:20–5:50', pct: 7, color: 'hsl(50, 80%, 55%)' },
  { id: 4, label: 'Z4 · THRESHOLD', paceRange: '4:56–5:20', pct: 12, color: 'hsl(35, 85%, 55%)' },
  { id: 5, label: 'Z5 · VO2MAX', paceRange: '< 4:56', pct: 6, color: 'hsl(15, 85%, 55%)' },
];

const ZONE_COLORS: Record<number, string> = {
  1: 'hsl(170, 80%, 45%)',
  2: 'hsl(160, 70%, 50%)',
  3: 'hsl(50, 80%, 55%)',
  4: 'hsl(35, 85%, 55%)',
  5: 'hsl(15, 85%, 55%)',
  6: 'hsl(350, 80%, 55%)',
  7: 'hsl(280, 65%, 60%)',
};

function formatPaceBound(val: number): string {
  // Assume pace metric values are in min/km (decimal minutes)
  const mins = Math.floor(val);
  const secs = Math.round((val - mins) * 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function PaceZonesHud({ className }: PaceZonesHudProps) {
  const { formatPaceFromMinPerKm, paceUnit, formatPaceRange } = useUnitSystem();
  const { data: zoneSystem, isLoading } = useZoneSystem('run', 'pace');

  // Build zones from API data or fallback to static
  // All paceRange values are built as min/km M:SS strings, then converted in render
  const zones = (() => {
    if (zoneSystem?.bands && zoneSystem.bands.length > 0) {
      return zoneSystem.bands.map((band) => ({
        id: band.zone_number,
        label: `Z${band.zone_number} · ${band.name.toUpperCase()}`,
        paceRange: `${formatPaceBound(band.lower_bound)}–${formatPaceBound(band.upper_bound)}`,
        pct: POLARIZED_TARGET_PCT[band.zone_number] ?? Math.round(100 / zoneSystem.bands.length),
        color: band.color ?? ZONE_COLORS[band.zone_number] ?? 'hsl(210, 30%, 50%)',
      }));
    }
    return FALLBACK_ZONES;
  })();

  const isFromApi = !!(zoneSystem?.bands && zoneSystem.bands.length > 0);
  // anchor_value is threshold pace in sec/km — convert to user's display unit
  const anchorDisplay = (() => {
    if (!isFromApi || zoneSystem!.anchor_value == null) return '—';
    const minPerKm = zoneSystem!.anchor_value / 60;
    return formatPaceFromMinPerKm(minPerKm);
  })();
  const sourceLabel = isFromApi
    ? `[SYS] ${zoneSystem!.method.toUpperCase()}-DERIVED · LT2 ${anchorDisplay}`
    : '[SYS] ZONES DERIVED FROM TRAINING DATA';

  return (
    <div className={cn(
      'rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[16px] p-4',
      'shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]',
      className
    )}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-1 h-1 rounded-full bg-[hsl(170,80%,45%)]" />
        <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-muted-foreground">
          PACE ZONE DISTRIBUTION
        </span>
        <span className="ml-auto text-[8px] font-mono tracking-wider text-white/20 uppercase">
          {isLoading ? 'LOADING' : isFromApi ? 'PERSISTED' : 'READ ONLY'}
        </span>
      </div>

      {/* Zone bars */}
      <div className="space-y-2.5">
        {zones.map((zone) => (
          <div key={zone.id} className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono tracking-wider text-[#A0A0A0]">
                {zone.label}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-mono tabular-nums text-white/40">
                  {`${formatPaceRange(zone.paceRange)}${paceUnit}`}
                </span>
                <span className="text-[10px] font-mono tabular-nums text-foreground w-7 text-right">
                  {zone.pct}%
                </span>
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${zone.pct}%`,
                  backgroundColor: zone.color,
                  boxShadow: `0 0 8px ${zone.color}40`,
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-white/[0.06]">
        <p className="text-[9px] font-mono text-white/25 tracking-wider text-center uppercase">
          {sourceLabel}
        </p>
      </div>
    </div>
  );
}
